/**
 * Stripe カード決済（サブスク / 単発）の手続き。
 */
import axios from 'axios'
import { getCurrentInstance, type Ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'
import { isAlreadySubscribedConflict } from '@/utils/payment/subscriptionConflict'

const PAYMENT_YAGIPRIME_RESULT = 'payment_yagiprime_result'

type HostVm = {
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $varuna: { endPoints: Record<string, string> }
  $cookies: { get: (key: string) => string | undefined }
}

export type StripeCardPaymentOpts = {
  isSubscription: Ref<boolean>
  amount: Ref<number>
  selectedIndex: Ref<number>
  notificationSetting: Ref<string>
  paymentMode: Ref<string>
  print_type: Ref<string>
  format_id: Ref<string>
  quickemailFormData: Ref<Record<string, unknown>>
  isLoading: Ref<boolean>
  isProcessing: Ref<boolean>
  isAlreadySubscribed: Ref<boolean>
  paymentIntent: Ref<Record<string, unknown> | null>
  quickemailHistoryId: Ref<number | null>
  cardNumber: Ref<any>
  stripe: Ref<any>
  emitSuccess: (payload: unknown) => void
  emitError: (payload: { message: string }) => void
}

export function useStripeCardPayment(opts: StripeCardPaymentOpts) {
  const vm = getCurrentInstance()!.proxy as unknown as HostVm
  const { user, getClient } = useAuth()

  function uid(): string {
    return getClient()?.user?.uid ?? user.value?.uid ?? ''
  }

  function email(): string {
    return getClient()?.user?.email ?? user.value?.email ?? ''
  }

  async function createSubscription(token: { id: string }) {
    if (opts.isProcessing.value) return
    opts.isProcessing.value = true
    opts.isLoading.value = true
    emitter.emit('loading_on')
    const stripeToken = token?.id || ''

    try {
      const formData = new FormData()
      formData.append('stripe_token', stripeToken)
      formData.append('uuid', uid())
      formData.append('subscription_type', String(opts.selectedIndex.value))
      formData.append('notification_setting', opts.notificationSetting.value)
      formData.append('app_flag', vm.$site.appFlag)
      formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
      formData.append('version', '3')
      formData.append('site_flag', 'r')
      formData.append('payment_method', 'card')

      const response = await axios.post(
        vm.$varuna.endPoints.subscription,
        formData,
        { timeout: 60000 }
      )

      if (response.data.requires_action) {
        // 無料期間つきの加入（紹介キャンペーン等）は初回請求が ¥0 になり、Stripe は
        // PaymentIntent ではなく SetupIntent を返す。決済ではなくカード登録の認証なので
        // confirmCardSetup を使う。ここを分けないと無料期間中に 3DS が未完のまま残り、
        // 無料期間が明けた初回課金が利用者不在で失敗する。
        const setupClientSecret = response.data.setup_intent_client_secret

        const { error: confirmError } = setupClientSecret
          ? await opts.stripe.value.confirmCardSetup(setupClientSecret, {
              payment_method: { card: opts.cardNumber.value }
            })
          : await opts.stripe.value.confirmCardPayment(
              response.data.payment_intent_client_secret,
              {
                payment_method: { card: opts.cardNumber.value },
                return_url: window.location.href
              }
            )
        if (confirmError) throw confirmError

        const confirmFormData = new FormData()
        if (setupClientSecret) {
          confirmFormData.append(
            'setup_intent_id',
            response.data.setup_intent_id
          )
        } else {
          confirmFormData.append(
            'payment_intent_id',
            response.data.payment_intent_id
          )
        }
        confirmFormData.append(
          'stripe_subscription_id',
          response.data.stripe_subscription_id
        )
        confirmFormData.append('uuid', uid())
        const confirmResponse = await axios.post(
          vm.$varuna.endPoints.confirmSubscription,
          confirmFormData
        )
        if (
          confirmResponse.status !== 200 ||
          (confirmResponse.data && confirmResponse.data.success === false)
        ) {
          const errorMessage =
            confirmResponse.data?.message ||
            'サブスクリプションの確認に失敗しました'
          const error: any = new Error(errorMessage)
          error.response = confirmResponse
          throw error
        }
      }

      if (response.status === 201 || response.status === 200) {
        const subformData = new FormData()
        const routeId = vm.$cookies.get('_YAGISH_YAGIPRIME_ROUTE_ID')
        subformData.append('route', routeId || '')
        subformData.append('uuid', uid())
        await axios.post(
          vm.$varuna.endPoints.setYagiprimeEntrySource,
          subformData,
          { timeout: 60000 }
        )
        emitter.emit(PAYMENT_YAGIPRIME_RESULT, 'success')
        opts.emitSuccess({ type: 'subscription', status: 'success' })
      }
    } catch (error: any) {
      if (isAlreadySubscribedConflict(error)) {
        opts.isAlreadySubscribed.value = true
        emitter.emit(PAYMENT_YAGIPRIME_RESULT, 'success')
        opts.emitSuccess({ type: 'subscription', status: 'already_exists' })
        return
      }

      let errorMessage = 'サブスクリプション作成に失敗しました'
      if (error.response?.status === 400) {
        if (error.response.data?.error_type === 'payment_failed') {
          errorMessage =
            error.response.data.message ||
            'カード決済に失敗しました。カード情報を確認してください。'
        }
      }
      if (error.response?.status === 500) {
        errorMessage =
          '決済処理中にエラーが発生しました。しばらく待ってから再度お試しください。'
      }
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      emitter.emit(PAYMENT_YAGIPRIME_RESULT, {
        status: 'error',
        message: errorMessage
      })
      opts.emitError({ message: errorMessage })
    } finally {
      opts.isLoading.value = false
      if (!opts.isAlreadySubscribed.value) {
        opts.isProcessing.value = false
      }
      emitter.emit('loading_off')
    }
  }

  async function createQuickmailIntent() {
    const formData = new FormData()
    formData.append('uuid', uid())
    formData.append('version', '4')
    formData.append('payment_method', 'card')
    formData.append('stripe_token', '')
    formData.append('merchant_payment_id', '')
    Object.keys(opts.quickemailFormData.value).forEach((key) => {
      const value = opts.quickemailFormData.value[key]
      if (value === null || value === undefined) return
      formData.append(key, value as string)
    })
    const response = await axios.post(
      vm.$varuna.endPoints.createQuickmailPaymentIntent,
      formData,
      { timeout: 60000 }
    )
    if (!response.data || response.data.status !== 'success') {
      const msg =
        response.data?.message ||
        'メール便用 PaymentIntent の作成に失敗しました'
      opts.emitError({ message: msg })
      throw new Error(msg)
    }
    opts.quickemailHistoryId.value = response.data.history_id
    opts.paymentIntent.value = {
      client_secret: response.data.clientSecret,
      id: response.data.paymentIntentId
    }
    return opts.paymentIntent.value
  }

  async function createPaymentIntent() {
    if (opts.paymentMode.value === 'quickemail') {
      return createQuickmailIntent()
    }
    try {
      const formData = new FormData()
      formData.append('uuid', uid())
      formData.append('amount', String(opts.amount.value))
      formData.append('currency', 'jpy')
      if (opts.paymentMode.value === 'eprint') {
        formData.append('capture_method', 'manual')
      }
      const response = await axios.post(
        vm.$varuna.endPoints.createPaymentIntent,
        formData
      )
      opts.paymentIntent.value = response.data
      return opts.paymentIntent.value
    } catch (error: any) {
      let errorMessage = '決済処理の開始に失敗しました'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      opts.emitError({ message: errorMessage })
      throw error
    }
  }

  async function savePaymentLogWithStripeId(
    paymentIntentId: string,
    options: {
      status?: boolean
      errorCode?: string | null
      errorMessage?: string | null
    } = {}
  ) {
    const { status = true, errorCode = null, errorMessage = null } = options
    try {
      const formData = new FormData()
      formData.append('uuid', uid())
      formData.append('payment_type', 'stripe')
      formData.append('payment_cost', String(opts.amount.value))
      formData.append('payment_status', status ? 'true' : 'false')
      formData.append('print_type', opts.print_type.value || '0')
      formData.append('format_id', opts.format_id.value || '')
      const detailObj: Record<string, unknown> = {
        payment_intent_id: paymentIntentId,
        timestamp: new Date().toISOString()
      }
      if (!status) {
        detailObj.error_code = errorCode
        detailObj.error_message = errorMessage
      }
      formData.append('detail', JSON.stringify(detailObj))
      const response = await axios.post(
        vm.$varuna.endPoints.logConveniencePayment,
        formData,
        { timeout: 30000 }
      )
      return response.data.payment_history_id
    } catch {
      // ログ保存失敗は決済処理には影響させない
    }
  }

  async function createPayment(token: { id: string }) {
    opts.quickemailHistoryId.value = null
    opts.paymentIntent.value = null
    try {
      opts.isLoading.value = true
      const paymentIntent = (await createPaymentIntent()) as any
      const { error: confirmError, paymentIntent: confirmedPaymentIntent } =
        await opts.stripe.value.confirmCardPayment(
          paymentIntent.client_secret,
          {
            payment_method: {
              card: opts.cardNumber.value,
              billing_details: {
                name: uid(),
                email: email()
              }
            },
            return_url: window.location.href
          }
        )

      if (confirmError) {
        let errorMessage = confirmError.message
        if (confirmError.code === 'insufficient_funds') {
          errorMessage =
            'カードの残高が不足しています。別のカードをお試しください。'
        } else if (confirmError.code === 'card_declined') {
          errorMessage =
            'カードが拒否されました。カード会社にお問い合わせください。'
        } else if (confirmError.code === 'expired_card') {
          errorMessage = 'カードの有効期限が切れています。'
        } else if (confirmError.code === 'incorrect_cvc') {
          errorMessage = 'セキュリティコード（CVC）が正しくありません。'
        } else if (confirmError.code === 'processing_error') {
          errorMessage =
            '決済処理中にエラーが発生しました。しばらく時間をおいて再度お試しください。'
        }
        const enhancedError: any = new Error(errorMessage)
        enhancedError.code = confirmError.code
        enhancedError.type = confirmError.type
        throw enhancedError
      }

      let paymentHistoryId = null
      if (opts.paymentMode.value !== 'quickemail') {
        try {
          paymentHistoryId = await savePaymentLogWithStripeId(
            confirmedPaymentIntent.id
          )
        } catch {
          // continue
        }
      }

      opts.emitSuccess({
        type: 'single',
        token,
        paymentIntent: confirmedPaymentIntent,
        paymentHistoryId,
        paymentIntentId: confirmedPaymentIntent.id,
        quickemailHistoryId:
          opts.paymentMode.value === 'quickemail'
            ? opts.quickemailHistoryId.value
            : null
      })
    } catch (error: any) {
      let errorMessage = '決済処理に失敗しました'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      opts.emitError({ message: errorMessage })
      if (opts.paymentMode.value !== 'quickemail') {
        const failedIntentId =
          (opts.paymentIntent.value && (opts.paymentIntent.value as any).id) ||
          error.payment_intent?.id ||
          'unknown'
        savePaymentLogWithStripeId(failedIntentId, {
          status: false,
          errorCode: error.code || 'unknown',
          errorMessage
        }).catch(() => {})
      }
    } finally {
      opts.isLoading.value = false
    }
  }

  function tokenCreated(token: { id: string }) {
    if (opts.isSubscription.value) {
      createSubscription(token).catch(() => {})
    } else {
      createPayment(token).catch(() => {})
    }
  }

  return {
    createSubscription,
    createPayment,
    createPaymentIntent,
    createQuickmailIntent,
    savePaymentLogWithStripeId,
    tokenCreated
  }
}
